import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Typography } from "@mui/material";
import {
  MIXED_VIEWPORT_LAYOUT_PRESETS,
  getViewportCount,
  normalizeViewportLayout,
  validateViewportGeometry,
} from '../viewportLayoutGeometry';

const MIXED_LAYOUT_OPTIONS = Object.values(MIXED_VIEWPORT_LAYOUT_PRESETS);

export const KAIROS_HP_FRIENDLY_PRESETS = 'KAIROS_HP_FRIENDLY_PRESETS';

let localIdCounter = 0;
const uniqueLocalId = (prefix) => {
  localIdCounter = (localIdCounter + 1) % Number.MAX_SAFE_INTEGER;
  return `${prefix}-${Date.now()}-${localIdCounter.toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
};

const baseViewport = (label, modality, prefer = [], fallback = 'first', extraSelector = {}) => ({
  label,
  seriesSelector: { modality, prefer, fallback, ...extraSelector },
  wl: { ww: 0, wc: 0 },
});

const normalizeModality = (value, fallback = 'MG') => String(value || fallback).trim().toUpperCase();

const emptyProtocol = (base) => ({
  id: uniqueLocalId('local'),
  name: base?.name ? `${base.name} Custom` : "Custom Protocol",
  // The matching engine adds priority to the protocol score, so higher values win.
  // Personal presets live in the validated 50-100 range: they can outrank most
  // bundled defaults without generating invalid >100 priorities from clones.
  priority: Math.min(100, Math.max(50, Number(base?.priority || 50))),
  matchingRules: { modalities: base?.matchingRules?.modalities || ["MG"], bodyPartKeywords: base?.matchingRules?.bodyPartKeywords || [] },
  layout: base?.layout || { rows: 2, cols: 2 },
  viewports: (base?.viewports || ["R CC", "L CC", "R MLO", "L MLO"].map(label => ({ label, seriesSelector: { modality: "MG", prefer: [label.toLowerCase()], fallback: "first" }, wl: { ww: 0, wc: 0 } }))).map(v => ({
    label: v.label || "",
    wl: v.wl || { ww: 0, wc: 0 },
    seriesSelector: { modality: v.seriesSelector?.modality || "MG", prefer: v.seriesSelector?.prefer || [], laterality: v.seriesSelector?.laterality || "", viewPosition: v.seriesSelector?.viewPosition || "", fallback: v.seriesSelector?.fallback || "first" },
  })),
});

export const makeFriendlyHangingProtocolPreset = (presetId, currentProtocol) => {
  const current = emptyProtocol(currentProtocol);
  const modality = normalizeModality(current.matchingRules?.modalities?.[0], 'CT');
  const bodyPartKeywords = current.matchingRules?.bodyPartKeywords || [];
  const base = {
    id: uniqueLocalId(`local-${presetId}`),
    priority: Math.max(75, current.priority || 75),
    matchingRules: { modalities: [modality], bodyPartKeywords },
  };

  if (presetId === 'clone-current') {
    return { ...current, id: uniqueLocalId('local-clone-current'), name: `${current.name || 'Current protocol'} Copy` };
  }
  if (presetId === 'ct-compare') {
    return {
      ...base,
      name: 'My CT 2x2 Compare',
      matchingRules: { modalities: ['CT'], bodyPartKeywords },
      layout: { rows: 2, cols: 2 },
      viewports: [
        baseViewport('Current axial', 'CT', ['axial', 'standard'], 'largest'),
        baseViewport('Prior axial', 'CT', ['prior', 'comparison'], 'second-largest'),
        baseViewport('Lung window', 'CT', ['lung'], 'largest'),
        baseViewport('Soft tissue', 'CT', ['soft', 'venous'], 'third-largest'),
      ],
    };
  }
  if (presetId === 'mr-planes') {
    return {
      ...base,
      name: 'My MR 2x2 Planes',
      matchingRules: { modalities: ['MR'], bodyPartKeywords },
      layout: { rows: 2, cols: 2 },
      viewports: [
        baseViewport('Axial fluid', 'MR', ['ax', 't2', 'fs', 'stir'], 'largest'),
        baseViewport('Coronal fluid', 'MR', ['cor', 't2', 'fs', 'stir'], 'second-largest'),
        baseViewport('Coronal T1', 'MR', ['cor', 't1'], 'third-largest'),
        baseViewport('Sagittal fluid', 'MR', ['sag', 't2', 'fs', 'stir'], 'fourth-largest'),
      ],
    };
  }
  if (presetId === 'mammo-2x2') {
    return {
      ...base,
      name: 'My Mammo 2x2',
      matchingRules: { modalities: ['MG'], bodyPartKeywords },
      layout: { rows: 2, cols: 2 },
      viewports: [
        baseViewport('R CC', 'MG', ['r cc', 'right cc'], 'first', { laterality: 'R', viewPosition: 'CC' }),
        baseViewport('L CC', 'MG', ['l cc', 'left cc'], 'first', { laterality: 'L', viewPosition: 'CC' }),
        baseViewport('R MLO', 'MG', ['r mlo', 'right mlo'], 'first', { laterality: 'R', viewPosition: 'MLO' }),
        baseViewport('L MLO', 'MG', ['l mlo', 'left mlo'], 'first', { laterality: 'L', viewPosition: 'MLO' }),
      ],
    };
  }
  if (presetId === 'single-review') {
    return {
      ...base,
      name: 'My Single Viewport Review',
      layout: { rows: 1, cols: 1 },
      viewports: [baseViewport('Primary series', modality, [], 'largest')],
    };
  }
  return current;
};

export const validateFriendlyHangingProtocol = (protocol) => {
  const issues = [];
  const name = String(protocol?.name || '').trim();
  const modalities = (protocol?.matchingRules?.modalities || []).filter(Boolean);
  const rows = Number(protocol?.layout?.rows || 0);
  const cols = Number(protocol?.layout?.cols || 0);
  const priority = Number(protocol?.priority || 0);
  const viewports = protocol?.viewports || [];
  const viewportCount = getViewportCount(protocol?.layout);
  const geometryValidation = protocol?.layout?.geometry ? validateViewportGeometry(protocol.layout.geometry) : { valid: true, issues: [] };
  const labels = viewports.map((v) => String(v?.label || '').trim()).filter(Boolean);
  const duplicateLabels = labels.filter((label, index) => labels.indexOf(label) !== index);

  if (!name) issues.push('Add a protocol name before saving.');
  if (!modalities.length) issues.push('Choose at least one matching modality.');
  if (priority < 1 || priority > 100) issues.push('Priority must be between 1 and 100 so personal presets can yield to department defaults.');
  if (rows * cols > 16 || viewportCount > 16) issues.push('Keep custom layouts at 16 viewports or fewer for diagnostic performance.');
  if (rows < 1 || cols < 1) issues.push('Rows and columns must be at least 1.');
  if (!geometryValidation.valid) issues.push(`Custom geometry is invalid: ${geometryValidation.issues.join(', ')}.`);
  if (viewports.length !== viewportCount) issues.push(`Layout requires exactly ${viewportCount} viewport assignments.`);
  if (labels.length !== viewports.length) issues.push('Every viewport needs a readable label.');
  if (duplicateLabels.length) issues.push(`Duplicate viewport labels: ${Array.from(new Set(duplicateLabels)).join(', ')}.`);
  return issues;
};

export const summarizeFriendlyHangingProtocol = (protocol) => {
  const modalities = (protocol?.matchingRules?.modalities || []).join(', ') || 'Any modality';
  const keywords = (protocol?.matchingRules?.bodyPartKeywords || []).join(', ') || 'no body-part keywords';
  const rows = protocol?.layout?.rows || 1;
  const cols = protocol?.layout?.cols || 1;
  const labels = (protocol?.viewports || []).map(v => v.label).filter(Boolean).join(' • ') || 'unlabeled viewports';
  return `${modalities} studies with ${keywords}; ${rows}×${cols} layout; ${labels}`;
};

const csv = (v) => Array.isArray(v) ? v.join(", ") : (v || "");
const arr = (v) => String(v || "").split(",").map(s => s.trim()).filter(Boolean);

const PRESETS = [
  ['clone-current', 'Clone current study', 'Clone the active protocol; inherits current matching rules'],
  ['ct-compare', 'CT 2×2 compare', 'CT preset: forces CT modality and starts with axial/prior/lung/soft-tissue slots'],
  ['mr-planes', 'MR 2×2 planes', 'MR preset: forces MR modality and starts with axial/coronal/sagittal plane slots'],
  ['mammo-2x2', 'Mammo 2×2', 'Mammography preset: forces MG modality with R/L CC/MLO selectors'],
  ['single-review', 'Single viewport review', 'Single viewport preset using the current modality'],
];

const HangingProtocolEditorDialog = ({ open, onClose, currentProtocol, overrides, onSave }) => {
  const [protocols, setProtocols] = React.useState(overrides || []);
  const [selectedId, setSelectedId] = React.useState("");
  const [draft, setDraft] = React.useState(emptyProtocol(currentProtocol));

  React.useEffect(() => {
    if (!open) return;
    setProtocols(overrides || []);
    const first = overrides?.[0] || emptyProtocol(currentProtocol);
    setSelectedId(first.id);
    setDraft(first);
  }, [open, overrides, currentProtocol]);

  const validationIssues = React.useMemo(() => validateFriendlyHangingProtocol(draft), [draft]);
  const summary = React.useMemo(() => summarizeFriendlyHangingProtocol(draft), [draft]);
  const selectableProtocols = protocols.length ? protocols : [draft];
  const layoutLimits = React.useMemo(() => {
    const rows = Math.max(1, Number(draft.layout?.rows || 1));
    const cols = Math.max(1, Number(draft.layout?.cols || 1));
    return {
      maxRows: Math.max(1, Math.floor(16 / cols)),
      maxCols: Math.max(1, Math.floor(16 / rows)),
    };
  }, [draft.layout?.rows, draft.layout?.cols]);

  const applyPreset = (presetId) => {
    const p = makeFriendlyHangingProtocolPreset(presetId, currentProtocol);
    setDraft(p);
    setSelectedId(p.id);
  };

  const setField = (key, value) => setDraft(d => ({ ...d, [key]: value }));
  const setRules = (key, value) => setDraft(d => ({ ...d, matchingRules: { ...(d.matchingRules || {}), [key]: value } }));
  const setLayout = (key, value) => setDraft(d => {
    const requested = Math.max(1, Number(value) || 1);
    const otherKey = key === 'rows' ? 'cols' : 'rows';
    const other = Math.max(1, Number(d.layout?.[otherKey] || 1));
    const maxForKey = Math.max(1, Math.floor(16 / other));
    const layout = normalizeViewportLayout({ rows: d.layout?.rows || 1, cols: d.layout?.cols || 1, [key]: Math.min(requested, maxForKey) });
    const count = getViewportCount(layout);
    const viewports = Array.from({ length: count }, (_, i) => d.viewports?.[i] || { label: `VP ${i + 1}`, seriesSelector: { modality: "MG", prefer: [], fallback: "first" }, wl: { ww: 0, wc: 0 } });
    return { ...d, layout, viewports };
  });
  const setLayoutPreset = (presetId) => setDraft(d => {
    if (presetId === 'rectangular') {
      return { ...d, layout: normalizeViewportLayout({ rows: d.layout?.rows || 2, cols: d.layout?.cols || 2 }) };
    }
    const preset = MIXED_VIEWPORT_LAYOUT_PRESETS[presetId];
    if (!preset) return d;
    const layout = normalizeViewportLayout(preset);
    const viewports = Array.from({ length: getViewportCount(layout) }, (_, i) => d.viewports?.[i] || { label: `VP ${i + 1}`, seriesSelector: { modality: normalizeModality(d.matchingRules?.modalities?.[0], 'MG'), prefer: [], fallback: "first" }, wl: { ww: 0, wc: 0 } });
    return { ...d, layout, viewports };
  });
  const setVp = (i, patch) => setDraft(d => ({ ...d, viewports: d.viewports.map((v, idx) => idx === i ? { ...v, ...patch } : v) }));
  const setSel = (i, patch) => setDraft(d => ({ ...d, viewports: d.viewports.map((v, idx) => idx === i ? { ...v, seriesSelector: { ...(v.seriesSelector || {}), ...patch } } : v) }));

  const upsert = () => {
    const clean = { ...draft, id: draft.id || uniqueLocalId('local'), priority: Number(draft.priority) || 50 };
    const next = [...protocols.filter(p => p.id !== clean.id), clean];
    setProtocols(next); setSelectedId(clean.id); onSave(next);
  };
  const remove = () => { const next = protocols.filter(p => p.id !== draft.id); setProtocols(next); onSave(next); const first = next[0] || emptyProtocol(currentProtocol); setDraft(first); setSelectedId(first.id); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: "#101010", color: "#ddd", border: "1px solid #263238" } }}>
      <DialogTitle sx={{ color: "#00bcd4", fontSize: 14, letterSpacing: 1 }}>HANGING PROTOCOL EDITOR</DialogTitle>
      <DialogContent dividers sx={{ borderColor: "#222" }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
          <Select size="small" value={selectedId} onChange={e => { const p = protocols.find(x => x.id === e.target.value); if (p) { setSelectedId(p.id); setDraft(p); } }} sx={{ minWidth: 220, color: "#ddd", fontSize: 12, ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" } }}>
            {selectableProtocols.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
          <Button size="small" type="button" onClick={() => { const p = emptyProtocol(currentProtocol); setDraft(p); setSelectedId(p.id); }} sx={{ color: "#00bcd4" }}>New from current</Button>
          <Button size="small" type="button" onClick={() => applyPreset('clone-current')} sx={{ color: "#8be9fd" }}>Reset draft to current protocol</Button>
        </Box>
        <Box data-kairos-marker={KAIROS_HP_FRIENDLY_PRESETS} role="group" aria-label="Friendly hanging protocol presets" sx={{ p: 1.25, mb: 1.5, border: "1px solid #17313a", borderRadius: 1, bgcolor: "rgba(0,188,212,0.06)" }}>
          <Typography sx={{ color: "#8be9fd", fontSize: 12, fontWeight: 700, mb: 0.75 }}>Friendly presets</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            {PRESETS.map(([id, label, title]) => (
              <Button key={id} type="button" title={title} aria-label={`Apply ${label} hanging protocol preset`} size="small" variant="outlined" onClick={() => applyPreset(id)} sx={{ color: "#d6f7ff", borderColor: "#2a5b66", fontSize: 11 }}>{label}</Button>
            ))}
          </Box>
          <Typography title={summary} noWrap sx={{ color: "#b8c7cc", fontSize: 11 }}>Apply a preset, customize it, then click Save to profile for matching studies. Summary: {summary}</Typography>
          <Typography sx={{ color: "#ffb74d", fontSize: 10, mt: 0.5 }}>Clone/reset inherits matching rules and will apply to all studies matching the modalities and keywords below.</Typography>
          {validationIssues.length > 0 && (
            <Box role="status" aria-live="polite" aria-label="Hanging protocol validation issues" sx={{ mt: 0.75 }}>
              {validationIssues.map(issue => <Typography key={issue} sx={{ color: "#ffb74d", fontSize: 11 }}>• {issue}</Typography>)}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.4fr 1fr 1fr", gap: 1, mb: 1.5 }}>
          <TextField label="Name" size="small" value={draft.name || ""} onChange={e => setField("name", e.target.value)} />
          <TextField label="Priority" size="small" type="number" inputProps={{ min: 1, max: 100 }} value={draft.priority || 75} onChange={e => setField("priority", Math.max(1, Math.min(100, Number(e.target.value) || 1)))} />
          <TextField label="Modalities" size="small" value={csv(draft.matchingRules?.modalities)} onChange={e => setRules("modalities", arr(e.target.value))} />
          <Select
            size="small"
            inputProps={{ 'aria-label': 'Viewport layout shape' }}
            value={draft.layout?.presetId || 'rectangular'}
            onChange={e => setLayoutPreset(e.target.value)}
          >
            <MenuItem value="rectangular">Rectangular grid</MenuItem>
            {MIXED_LAYOUT_OPTIONS.map(option => <MenuItem key={option.presetId} value={option.presetId}>{option.label}</MenuItem>)}
          </Select>
          <TextField label="Rows" size="small" type="number" disabled={Boolean(draft.layout?.geometry)} inputProps={{ min: 1, max: layoutLimits.maxRows }} value={draft.layout?.rows || 1} onChange={e => setLayout("rows", e.target.value)} />
          <TextField label="Cols" size="small" type="number" disabled={Boolean(draft.layout?.geometry)} inputProps={{ min: 1, max: layoutLimits.maxCols }} value={draft.layout?.cols || 1} onChange={e => setLayout("cols", e.target.value)} />
        </Box>
        <TextField fullWidth label="Body/study keywords" size="small" value={csv(draft.matchingRules?.bodyPartKeywords)} onChange={e => setRules("bodyPartKeywords", arr(e.target.value))} sx={{ mb: 1.5 }} />
        <Typography sx={{ fontSize: 11, color: "#777", mb: 1 }}>Viewport assignment rules support mammography-style laterality/view-position matching where metadata or series descriptions expose R/L and CC/MLO.</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${draft.layout?.cols || 1}, minmax(220px, 1fr))`, gap: 1 }}>
          {(draft.viewports || []).map((vp, i) => (
            <Box key={i} sx={{ p: 1, border: "1px solid #222", bgcolor: "#0b0b0b" }}>
              <Typography sx={{ color: "#00bcd4", fontSize: 11, mb: 1 }}>VIEWPORT {i + 1}</Typography>
              <TextField fullWidth label="Label" size="small" value={vp.label || ""} onChange={e => setVp(i, { label: e.target.value })} sx={{ mb: 1 }} />
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <TextField label="Modality" size="small" value={vp.seriesSelector?.modality || ""} onChange={e => setSel(i, { modality: e.target.value })} />
                <TextField label="Laterality" size="small" placeholder="R/L" value={vp.seriesSelector?.laterality || ""} onChange={e => setSel(i, { laterality: e.target.value.toUpperCase() })} />
                <TextField label="View" size="small" placeholder="CC/MLO" value={vp.seriesSelector?.viewPosition || ""} onChange={e => setSel(i, { viewPosition: e.target.value.toUpperCase() })} />
                <TextField label="Fallback" size="small" value={vp.seriesSelector?.fallback || "first"} onChange={e => setSel(i, { fallback: e.target.value })} />
              </Box>
              <TextField fullWidth label="Prefer keywords" size="small" value={csv(vp.seriesSelector?.prefer)} onChange={e => setSel(i, { prefer: arr(e.target.value) })} sx={{ mt: 1 }} />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button type="button" aria-label="Delete saved hanging protocol override" disabled={!protocols.find(p => p.id === draft.id)} onClick={remove} sx={{ color: "#f44336" }}>Delete saved override</Button>
        <Box><Button onClick={onClose} sx={{ color: "#777" }}>Close</Button><Button disabled={validationIssues.length > 0} onClick={upsert} sx={{ color: "#00bcd4" }}>Save to profile</Button></Box>
      </DialogActions>
    </Dialog>
  );
};

export const __test__ = {
  uniqueLocalId,
  makeFriendlyHangingProtocolPreset,
  validateFriendlyHangingProtocol,
  summarizeFriendlyHangingProtocol,
};

export default HangingProtocolEditorDialog;
